/**
 * PATH: packages/document-editor/src/PaginatedPageShell.tsx
 * FILE_ID: DOCEDITOR-PAGINATED-SHELL-001
 * PURPOSE: Measured A4 editing canvas with typed zoom and page navigation.
 * WHY: Editing pagination must be explicitly distinct from exact PDF preview.
 * RELATED: DocumentEditor.tsx, contracts.ts
 */
import type { PageSettings } from './contracts'
import { usePageCanvasGeometry } from './useDocumentPagination'

export interface PageNavigationState {
  activePage: number
  pageCount: number
}

export interface PaginatedPageShellProps {
  pageSettings: PageSettings
  zoom: number
  navigation: PageNavigationState
  onZoomChange: (zoom: number) => void
  onPageChange: (page: number) => void
  onPageSettingsChange?: (settings: PageSettings) => void
  onOpenExactPdfPreview?: () => void
  paperRef?: React.RefObject<HTMLDivElement | null>
  viewportRef?: React.RefObject<HTMLDivElement | null>
  toolbar?: React.ReactNode
  children: React.ReactNode
}

export function PaginatedPageShell({
  pageSettings,
  zoom,
  navigation,
  onZoomChange,
  onPageChange,
  onPageSettingsChange,
  onOpenExactPdfPreview,
  paperRef,
  viewportRef,
  toolbar,
  children,
}: PaginatedPageShellProps) {
  const page = Math.min(Math.max(navigation.activePage, 1), Math.max(navigation.pageCount, 1))
  const geometry = usePageCanvasGeometry(pageSettings, zoom, navigation.pageCount)
  return (
    <section
      className="qdoc-page-shell overflow-hidden rounded-lg border border-slate-300 bg-slate-100"
      aria-label="Paginated document editor"
      onKeyDown={(event) => {
        if (!event.ctrlKey || !['PageUp', 'PageDown'].includes(event.key)) return
        event.preventDefault()
        onPageChange(page + (event.key === 'PageDown' ? 1 : -1))
      }}
    >
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-300 bg-white px-3 py-2 text-sm">
        <label className="qdoc-control">
          Zoom
          <select aria-label="Document zoom" value={zoom} onChange={(event) => onZoomChange(Number(event.target.value))}>
            {[50, 75, 100, 125, 150].map((value) => <option key={value} value={value}>{value}%</option>)}
          </select>
        </label>
        <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>Previous page</button>
        <label className="qdoc-control">
          Page
          <select aria-label="Active page" value={page} onChange={(event) => onPageChange(Number(event.target.value))}>
            {Array.from({ length: navigation.pageCount }, (_, index) => (
              <option key={index + 1} value={index + 1}>{index + 1}</option>
            ))}
          </select>
          <span aria-live="polite">of {Math.max(navigation.pageCount, 1)}</span>
        </label>
        <button type="button" disabled={page >= navigation.pageCount} onClick={() => onPageChange(page + 1)}>Next page</button>
        {onPageSettingsChange && (
          <details className="qdoc-page-settings">
            <summary>Page settings</summary>
            <div className="qdoc-page-settings-panel">
              <label>
                Orientation
                <select
                  value={pageSettings.orientation}
                  onChange={(event) => onPageSettingsChange({
                    ...pageSettings,
                    orientation: event.target.value as PageSettings['orientation'],
                  })}
                >
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </label>
              {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
                <label key={side}>
                  {side[0].toUpperCase() + side.slice(1)} margin (mm)
                  <input
                    type="number"
                    min={0}
                    max={60}
                    value={pageSettings.marginsMm[side]}
                    onChange={(event) => onPageSettingsChange({
                      ...pageSettings,
                      marginsMm: {
                        ...pageSettings.marginsMm,
                        [side]: Math.min(60, Math.max(0, Number(event.target.value))),
                      },
                    })}
                  />
                </label>
              ))}
            </div>
          </details>
        )}
        {onOpenExactPdfPreview && (
          <button type="button" className="ml-auto" onClick={onOpenExactPdfPreview}>
            Exact PDF preview
          </button>
        )}
        <span className="text-xs text-slate-500">Exact PDF verifies final pagination</span>
      </header>
      {toolbar}
      <div ref={viewportRef} className="qdoc-page-viewport overflow-auto p-6" tabIndex={0} aria-label="Document pages">
        <div
          className="qdoc-scaled-paper mx-auto"
          style={{ width: geometry.scaledWidthPx, height: geometry.scaledHeightPx }}
        >
        <div
          ref={paperRef}
          className="qdoc-a4-page bg-white shadow-md"
          data-page-size="A4"
          data-page-count={navigation.pageCount}
          style={geometry.paperStyle}
        >
          {children}
        </div>
        </div>
      </div>
    </section>
  )
}
